import { AsyncPipe } from '@angular/common';
import { animate, query, style, transition, trigger } from '@angular/animations';
import { Component, inject, OnInit } from '@angular/core';
import { ResolveEnd, ResolveStart, Router, RouterOutlet } from '@angular/router';
import { SpinnerComponent, ToastComponent } from '@catch/general-ui';
import { ThemingService, ToastsService } from '@catch/services';
import { TranslocoService } from '@jsverse/transloco';
import { NgSelectConfig } from '@ng-select/ng-select';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { merge, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { fadeInOut, fadeInOutUp } from '../assets/animations/animations';
import { FooterComponent } from './components/static/footer/footer.component';
import { HeaderComponent } from './components/static/header/header.component';
import { environment } from '../environments/environment';
import { ColorService } from './services/color.service';
import { EmbedService } from './services/embed.service';
import { RefService } from './services/ref.service';
import { SEOService } from './services/seo.service';
import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';
import deLocale from 'i18n-iso-countries/langs/de.json';
import frLocale from 'i18n-iso-countries/langs/fr.json';


@UntilDestroy()
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: [
    trigger('routeAnimations', [
      transition('* => *', [
        query(
          ':leave',
          style({
            opacity: 1,
            position: 'absolute',
            width: '100%',
          }),
          { optional: true }
        ),
        query(
          ':enter',
          style({
            opacity: 0,
            position: 'absolute',
            width: '100%',
          }),
          { optional: true }
        ),
        query(':leave', animate('100ms', style({ opacity: 0 })), {
          optional: true,
        }),
        query(':enter', animate('100ms', style({ opacity: 1 })), {
          optional: true,
        }),
      ]),
    ]),
    fadeInOutUp,
    fadeInOut,
  ],
  imports: [
    AsyncPipe,
    RouterOutlet,
    HeaderComponent,
    FooterComponent,
    SpinnerComponent,
    ToastComponent,
  ],
})
export class AppComponent implements OnInit {
  private readonly colorService = inject(ColorService);
  private readonly ngSelectConfig = inject(NgSelectConfig);
  private readonly refService = inject(RefService);
  private readonly router = inject(Router);
  private readonly seoService = inject(SEOService);
  private readonly themingService = inject(ThemingService);
  private readonly translateService = inject(TranslocoService);
  public readonly embedService = inject(EmbedService);
  public readonly toastsService = inject(ToastsService);

  loading$: Observable<boolean>;
  private showLoading$: Observable<boolean>;
  private hideLoading$: Observable<boolean>;

  constructor() {
    this.showLoading$ = this.router.events.pipe(
      filter(event => event instanceof ResolveStart),
      map(() => true)
    );
    this.hideLoading$ = this.router.events.pipe(
      filter(event => event instanceof ResolveEnd),
      map(() => false)
    );
    this.loading$ = merge(this.showLoading$, this.hideLoading$);

    this.themingService.setLightTheme();

    this.seoService.updateKeywords(['jobbörse', 'jobsuche', 'karriere', 'bewerbung', 'stellenangebot', 'jobangebot']);
    this.colorService.setDefaultDesign();
  }

  ngOnInit(): void {
    if (!environment.debug) {
      this.translateService
        .selectTranslate(['ct-library.user-console-stop', 'ct-library.user-console-warning'])
        .pipe(untilDestroyed(this))
        .subscribe({
          next: result => {
            console.info(
              `%c${result['ct-library.user-console-stop']}`,
              'color: red; font-size: 30px; font-weight: bold;'
            );
            console.info(`%c${result['ct-library.user-console-warning']}`, 'font-size: 20px; font-weight: bold;');
          },
        });
    }

    this.translateService
      .selectTranslate('ct-library.constants.ng-select.placeholders.not-found')
      .pipe(untilDestroyed(this))
      .subscribe({
        next: result => {
          this.ngSelectConfig.notFoundText = result;
        },
      });

    countries.registerLocale(enLocale);
    countries.registerLocale(deLocale);
    countries.registerLocale(frLocale);
  }

  prepareRoute(outlet: RouterOutlet) {
    return outlet.isActivated ? outlet.activatedRoute : '';
  }
}
